import { FC, ReactElement, useState } from 'react';
import * as Yup from 'yup';
import i18next from 'i18next';
import { EntityScope } from '../../../../shared/utils/enums';
import { ExerciseCategoryDto } from '../../../../shared/dtos/exerciseCategory.dto';
import { ExerciseCategoryRequest } from '../../../../shared/requests/exerciseCategory.request';
import { Alert, Button, Form, Spinner, Stack } from 'react-bootstrap';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { HttpMethod, useAuthFetchFromRestAPI } from '../../util/api';

type FormValues = {
  germanTitle: string;
  englishTitle: string;
  isActive: boolean;
}

type ExerciseCategoryFormProps = {
  initialData?: ExerciseCategoryDto;
  entityScope: EntityScope;
  onClose: () => void;
  onSubmit: (exerciseCategory: ExerciseCategoryDto) => void;
}

const ExerciseCategoryForm: FC<ExerciseCategoryFormProps> = ({
  initialData,
  entityScope,
  onSubmit,
  onClose
}: ExerciseCategoryFormProps): ReactElement => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { authFetch } = useAuthFetchFromRestAPI();

  const initialValues: FormValues = {
    germanTitle: initialData?.de ?? '',
    englishTitle: initialData?.en ?? '',
    isActive: initialData?.isActive ?? true,
  };

  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    germanTitle: Yup.string().required(i18next.t('generic.required')),
    englishTitle: Yup.string().required(i18next.t('generic.required')),
    isActive: Yup.boolean().default(true),
  });

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);

    try {

      const payload: ExerciseCategoryRequest = createInput(values);
      const result: ExerciseCategoryDto = await submitData(payload);
      onSubmit(result);

    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  const createInput = (values: FormValues): ExerciseCategoryRequest => ({
    de: values.germanTitle.trim(),
    en: values.englishTitle.trim(),
    isActive: values.isActive,
  });

  const submitData = async (payload: ExerciseCategoryRequest): Promise<ExerciseCategoryDto> => {
    const scope: string = entityScope === EntityScope.CORE ? 'core' : 'tenant';

    const url: string = initialData
      ? `/api/v1/exercise/category/${scope}/${encodeURIComponent(initialData.id)}`
      : `/api/v1/exercise/category/${scope}`;

    const method: HttpMethod = initialData ? 'PATCH' : 'POST';

    return authFetch(url, { method: method, body: payload });
  };


  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}

      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, touched, errors, handleSubmit, handleChange, handleBlur, isSubmitting }: FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="p-2"
            style={{ opacity: isSaving ? 0.5 : 1 }}>
            <Stack gap={3}>
              <Form.Group controlId="germanTitle">
                <Form.Label>
                  {i18next.t('exerciseCategory.germanTitle')}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="germanTitle"
                  value={values.germanTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.germanTitle && !!errors.germanTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.germanTitle}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="englishTitle">
                <Form.Label>
                  {i18next.t('exerciseCategory.englishTitle')}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="englishTitle"
                  value={values.englishTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.englishTitle && !!errors.englishTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.englishTitle}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="isActive">
                <Form.Check
                  type="checkbox"
                  name="isActive"
                  label={i18next.t('exerciseCategory.isActive')}
                  checked={values.isActive}
                  onChange={handleChange}
                />
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting || isSaving}>
                  {i18next.t('generic.close')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || isSaving}>
                  {i18next.t(initialData ? 'generic.save' : 'generic.create')}
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ExerciseCategoryForm;
